/**
 * Header notification bell (decision 84): an icon button the size of the header's other buttons, with an unread count,
 * opening a small panel with the latest notifications. Clicking one opens its link and marks it read; "Mark all read".
 */
import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { cn } from '../cn';

export interface BellItem {
  id: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: number;
  readAt: number | null;
}

/** "just now", "5 min ago", "3 h ago", "2 d ago". */
export function timeAgo(at: number, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}

export function NotificationBell({ items, unread, onOpen, onRead, onReadAll, onNavigate, settingsHref = '/settings#notifications', defaultOpen = false }: {
  items: BellItem[] | null;
  unread: number;
  /** The panel was opened (load the latest). */
  onOpen?: () => void;
  onRead: (id: string) => void;
  onReadAll: () => void;
  onNavigate: (href: string) => void;
  settingsHref?: string;
  /** For tests and previews. */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('keydown', key); };
  }, [open]);
  const count = unread > 99 ? '99+' : String(unread);
  return (
    <div ref={ref} className="relative">
      <button aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} title="Notifications" aria-expanded={open} aria-haspopup="dialog"
        onClick={() => { const next = !open; setOpen(next); if (next) onOpen?.(); }}
        className="relative rounded-xl border border-border bg-card p-2 shadow-sm hover:bg-secondary">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span aria-hidden="true" className="absolute -right-1 -top-1 min-w-[1rem] rounded-full bg-primary px-1 text-center text-[10px] font-bold leading-4 text-primary-foreground">{count}</span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 top-11 z-40 w-80 max-w-[calc(100vw-2rem)] animate-zoom-in rounded-2xl border border-border bg-card p-2 shadow-2xl">
          <div className="flex items-center justify-between border-b border-border px-2.5 pb-2 pt-1">
            <span className="text-sm font-bold">Notifications</span>
            {unread > 0 && <button onClick={onReadAll} className="text-xs font-semibold text-primary hover:underline">Mark all read</button>}
          </div>
          <ul className="max-h-96 overflow-y-auto py-1">
            {items === null ? <li className="px-2.5 py-3 text-xs text-muted-foreground">Loading…</li>
              : !items.length ? <li className="px-2.5 py-3 text-xs text-muted-foreground">Nothing yet. Finished transcriptions, scans and account updates show up here.</li>
              : items.map((n) => (
                <li key={n.id}>
                  <button onClick={() => { if (!n.readAt) onRead(n.id); if (n.link) { setOpen(false); onNavigate(n.link); } }}
                    className={cn('flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-secondary', !n.readAt && 'bg-primary/5')}>
                    <span aria-hidden="true" className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-primary')} />
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-xs', n.readAt ? 'font-medium' : 'font-bold')}>{n.title}</span>
                      {n.body && <span className="mt-0.5 block text-xs text-muted-foreground">{n.body}</span>}
                      <span className="mt-0.5 block text-[10px] text-muted-foreground">{timeAgo(n.createdAt)}{!n.readAt && <span className="sr-only"> · unread</span>}</span>
                    </span>
                  </button>
                </li>
              ))}
          </ul>
          <div className="border-t border-border px-2.5 pt-2">
            <button onClick={() => { setOpen(false); onNavigate(settingsHref); }} className="text-xs font-semibold text-muted-foreground hover:text-foreground">Notification settings</button>
          </div>
        </div>
      )}
    </div>
  );
}
